import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

export interface ButtonVariantOptions {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | undefined
  size?: 'sm' | 'md' | 'lg' | undefined
  fullWidth?: boolean | undefined
  className?: string | undefined
}

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonVariantOptions {}

const baseClasses =
  'inline-flex items-center justify-center font-medium rounded-sm transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none cursor-pointer select-none text-center'

const variantClasses = {
  primary:
    'bg-btn-primary text-btn-primary-fg hover:bg-btn-primary-hover active:opacity-90 shadow-xs',
  secondary:
    'bg-surface-2 text-fg hover:bg-surface-3 active:bg-surface-4',
  outline:
    'border border-border text-fg hover:bg-surface-1 hover:border-border-strong active:bg-surface-2',
  ghost:
    'text-fg-subtle hover:text-fg hover:bg-surface-1 active:bg-surface-2',
}

const sizeClasses = {
  sm: 'text-xs px-3.5 py-1.5 gap-1.5 min-h-[32px]',
  md: 'text-sm px-5 py-2.5 gap-2 min-h-[40px]',
  lg: 'text-sm sm:text-base px-6 sm:px-7 py-3.5 gap-2 min-h-[48px]',
}

export function buttonVariants({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: ButtonVariantOptions = {}): string {
  return cn(
    baseClasses,
    variantClasses[variant ?? 'primary'],
    sizeClasses[size ?? 'md'],
    fullWidth && 'w-full',
    className
  )
}

// Preserve dictionary access for backwards compatibility
buttonVariants.base = baseClasses
buttonVariants.variants = variantClasses
buttonVariants.sizes = sizeClasses

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      disabled,
      type = 'button',
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        className={buttonVariants({ variant, size, fullWidth, className })}
        {...props}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
